import { ModuleWithProviders } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { ErrorComponent, PagedTableWrapperComponent } from '@mango/core';

// Components for Routing
import { AUTHORIZATION_ROUTES } from '@mango/authorization';
import { AuthGuard } from '@mango/authentication';

import { UserCollectionComponent } from './user/user-collection.component';
import { UserFilterComponent } from './user/user-filter.component';
import { UserComponent } from './user/user.component';
import { DashboardComponent } from './dashboard/dashboard.component';

import { BaseLayoutComponent } from './shared/components/base-layout/base-layout.component';
import { CIJLoginComponent } from './login/login.component';
import { CIJActivateAccountComponent } from './login/activate-account/activate-account.component';
import { PreloadAllModulesWithDelay } from './preload';
import { AlertCollectionComponent } from './alert/alert-collection.component';
import { AlertFilterComponent } from './alert/alert-filter.component';
import { AlertComponent } from './alert/alert.component';
import { SupportComponent } from './support/support.component';
import { PrijscorrectieComponent } from './cij/prijscorrectie/prijscorrectie.component';
import { ExactComponent } from './cij/exact/exact.component';
import { PrijscorrectieArtikelComponent } from './cij/prijscorrectieArtikel/prijscorrectieArtikel.component';
import { IPCheckGuard } from './core/utils/ipcheck-guard';

const routes: Routes = [
    {
        path: '',
        component: BaseLayoutComponent,
        canActivate: [AuthGuard, IPCheckGuard],
        children: [
			{
                path: 'relatieUser',
                loadChildren: () => import('./relatie-user/relatie-user.module').then(m => m.RelatieUserModule) 
            },
			{
                path: 'kpCertificaat',
                loadChildren: () => import('./cij/kp-certificaat/kp-certificaat.module').then(m => m.kpCertificaatModule) 
            },
			{
                path: 'kpInstrument',
                loadChildren: () => import('./cij/kp-instrument/kp-instrument.module').then(m => m.kpInstrumentModule) 
            },
			{
                path: 'factuur',
                loadChildren: () => import('./cij/factuur/factuur.module').then(m => m.FactuurModule) 
            },
			{
                path: 'factuurregel',
                loadChildren: () => import('./cij/factuurregel/factuurregel.module').then(m => m.FactuurregelModule) 
            },
            {
                path: 'factuurStatusHistorie',
                loadChildren: () => import('./cij/factuurStatusHistorie/factuurStatusHistorie.module').then(m => m.FactuurStatusHistorieModule) 
            },
			{
                path: 'pakbon',
                loadChildren: () => import('./cij/pakbon/pakbon.module').then(m => m.PakbonModule) 
            },
			{
                path: 'pakbonregel',
                loadChildren: () => import('./cij/pakbonregel/pakbonregel.module').then(m => m.PakbonregelModule) 
            },
			{
                path: 'oproepHistorie',
                loadChildren: () => import('./cij/oproep-historie/oproep-historie.module').then(m => m.OproepHistorieModule) 
            },
			{
                path: 'vertaalTabel',
                loadChildren: () => import('./cij/vertaal-tabel/vertaal-tabel.module').then(m => m.VertaalTabelModule) 
            },
			{
                path: 'projectregelStatusHistorie',
                loadChildren: () => import('./cij/projectregel-status-historie/projectregel-status-historie.module').then(m => m.ProjectregelStatusHistorieModule) 
            },
			{
                path: 'projectregelArtikel',
                loadChildren: () => import('./cij/projectregel-artikel/projectregel-artikel.module').then(m => m.ProjectregelArtikelModule) 
            },
			{
                path: 'projectregel',
                loadChildren: () => import('./cij/projectregel/projectregel.module').then(m => m.ProjectregelModule) 
            },
			{
                path: 'postType',
                loadChildren: () => import('./cij/post-type/post-type.module').then(m => m.PostTypeModule) 
            },
			{
                path: 'project',
                loadChildren: () => import('./cij/project/project.module').then(m => m.ProjectModule) 
            },
			{
                path: 'projectgroep',
                loadChildren: () => import('./cij/projectgroep/projectgroep.module').then(m => m.ProjectgroepModule) 
            },
			{
                path: 'taal',
                loadChildren: () => import('./cij/taal/taal.module').then(m => m.TaalModule) 
            },
			{
                path: 'tekst',
                loadChildren: () => import('./cij/tekst/tekst.module').then(m => m.TekstModule) 
            },
			{
                path: 'volgblad',
                loadChildren: () => import('./cij/volgblad/volgblad.module').then(m => m.VolgbladModule) 
            },
			{
                path: 'voorblad',
                loadChildren: () => import('./cij/voorblad/voorblad.module').then(m => m.VoorbladModule) 
            },
			{
                path: 'instrument',
                loadChildren: () => import('./cij/instrument/instrument.module').then(m => m.InstrumentModule) 
            },
			{
                path: 'typeCalculatie',
                loadChildren: () => import('./cij/type-calculatie/type-calculatie.module').then(m => m.TypeCalculatieModule) 
            },
			{
                path: 'instrumentPrijsHistorie',
                loadChildren: () => import('./cij/instrument-prijs-historie/instrument-prijs-historie.module').then(m => m.InstrumentPrijsHistorieModule) 
            },
			{
                path: 'instrumentDefinitie',
                loadChildren: () => import('./cij/instrument-definitie/instrument-definitie.module').then(m => m.InstrumentDefinitieModule) 
            },
			{
                path: 'soort',
                loadChildren: () => import('./cij/soort/soort.module').then(m => m.SoortModule) 
            },
			{
                path: 'merk',
                loadChildren: () => import('./cij/merk/merk.module').then(m => m.MerkModule) 
            },
			{
                path: 'soortTemp',
                loadChildren: () => import('./cij/soort-temp/soort-temp.module').then(m => m.SoortTempModule) 
            },
			{
                path: 'verzendTarievenPrijzen',
                loadChildren: () => import('./cij/verzend-tarieven-prijzen/verzend-tarieven-prijzen.module').then(m => m.VerzendTarievenPrijzenModule) 
            },
			{
                path: 'artikelVerkoopPrijzen',
                loadChildren: () => import('./cij/artikel-verkoop-prijzen/artikel-verkoop-prijzen.module').then(m => m.ArtikelVerkoopPrijzenModule) 
            },
			{
                path: 'artikelLeverancierPrijs',
                loadChildren: () => import('./cij/artikel-leverancier-prijs/artikel-leverancier-prijs.module').then(m => m.ArtikelLeverancierPrijsModule) 
            },
			{
                path: 'artikel',
                loadChildren: () => import('./cij/artikel/artikel.module').then(m => m.ArtikelModule) 
            },
			{
                path: 'artikelgroep',
                loadChildren: () => import('./cij/artikelgroep/artikelgroep.module').then(m => m.ArtikelgroepModule) 
            },
			{
                path: 'artikelLeverancier',
                loadChildren: () => import('./cij/artikel-leverancier/artikel-leverancier.module').then(m => m.ArtikelLeverancierModule) 
            },
			{
                path: 'bedrijf',
                loadChildren: () => import('./cij/bedrijf/bedrijf.module').then(m => m.BedrijfModule) 
            },
			{
                path: 'btw',
                loadChildren: () => import('./cij/btw/btw.module').then(m => m.BtwModule) 
            },
			{
                path: 'verzendTarieven',
                loadChildren: () => import('./cij/verzend-tarieven/verzend-tarieven.module').then(m => m.VerzendTarievenModule) 
            },
			{
                path: 'afdeling',
                loadChildren: () => import('./cij/afdeling/afdeling.module').then(m => m.AfdelingModule) 
            },
			{
                path: 'personeel',
                loadChildren: () => import('./cij/personeel/personeel.module').then(m => m.PersoneelModule) 
            },
			{
                path: 'conditie',
                loadChildren: () => import('./cij/conditie/conditie.module').then(m => m.ConditieModule) 
            },
			{
                path: 'rubriek',
                loadChildren: () => import('./cij/rubriek/rubriek.module').then(m => m.RubriekModule) 
            },
			{
                path: 'adres',
                loadChildren: () => import('./cij/adres/adres.module').then(m => m.AdresModule) 
            },
			{
                path: 'relatie',
                loadChildren: () => import('./cij/relatie/relatie.module').then(m => m.RelatieModule) 
            },
			{
                path: 'factuurType',
                loadChildren: () => import('./cij/factuur-type/factuur-type.module').then(m => m.FactuurTypeModule) 
            },
			{
                path: 'land',
                loadChildren: () => import('./cij/land/land.module').then(m => m.LandModule) 
            },
			{
                path: 'contactpersoon',
                loadChildren: () => import('./cij/contactpersoon/contactpersoon.module').then(m => m.ContactpersoonModule) 
            },
            {
                path: '',
                redirectTo: '/dashboard',
                pathMatch: 'full'
            },
            {
                path: 'dashboard',
                component: DashboardComponent,
            },
            {
                path: '',
                redirectTo: '/exact',
                pathMatch: 'full'
            },
            {
                path: 'exact',
                component: ExactComponent,
            },
            {
                path: '',
                redirectTo: '/support',
                pathMatch: 'full'
            },
            {
                path: 'support',
                component: SupportComponent,
            },
            {
                path: '',
                redirectTo: '/prijscorrectie',
                pathMatch: 'full'
            },
            {
                path: 'prijscorrectie',
                component: PrijscorrectieComponent,
            },
            {
                path: '',
                redirectTo: '/prijscorrectieArtikel',
                pathMatch: 'full'
            },
            {
                path: 'prijscorrectieArtikel',
                component: PrijscorrectieArtikelComponent,
            },
            {
                path: 'authorization',
                children: AUTHORIZATION_ROUTES
            },
            {
                path: 'users',
                children: [
                    {
                        path: '',
                        redirectTo: '50/0/2',
                        pathMatch: 'full'
                    },
                    {
                        path: ':ps/:pi/:sort',
                        component: PagedTableWrapperComponent,
                        data: {
                            collection: UserCollectionComponent,
                            filter: UserFilterComponent,
                            title: "Gebruikers",
                            newItemRouterLink: ['/users/detail', 'new']
                        }
                    },
                    {
                        path: 'detail/:id',
                        component: UserComponent
                    }
                ]
            },
            {
                path: 'alerts',
                children: [
                    {
                        path: '',
                        redirectTo: '50/0/-2',
                        pathMatch: 'full'
                    },
                    {
                        path: ':ps/:pi/:sort',
                        component: PagedTableWrapperComponent,
                        data: {
                            collection: AlertCollectionComponent,
                            filter: AlertFilterComponent,
                            title: "Berichten",
                            newItemRouterLink: ['/alerts/detail', 'new']
                        }
                    },
                    {
                        path: 'detail/:id',
                        component: AlertComponent
                    }
                ]
            },
        ]
    },
    {
        path: 'report',
        loadChildren: () => import('./reporting/reporting.module').then(m => m.ReportingModule) 
    },
    {
        path: 'error',
        component: ErrorComponent
    },
    {
        path: 'activateAccount/:code',
        component: CIJActivateAccountComponent
    },
    {
        path: 'login',
        component: CIJLoginComponent
    }
];

// add a default route at the end that matches all other routes and redirect to 404
// routes.push(
//     {
//         path: '**',
//         redirectTo: '/error?msg=404 Not found&t=warning'
//     });

export const routing: ModuleWithProviders<RouterModule> = RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModulesWithDelay, relativeLinkResolution: 'legacy' });
